import React from "react";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { clearAuthSession, getAuthSession, getCurrentRole } from "../auth";
import { navigationItems } from "./listItems";

const drawerWidth = 280;

export default function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const session = getAuthSession();
  const currentRole = getCurrentRole();

  const handleLogout = () => {
    clearAuthSession();
    navigate("/login", { replace: true });
  };

  const groupedItems = navigationItems
    .filter((item) => !item.allowedRoles || item.allowedRoles.includes(currentRole))
    .reduce((groups, item) => {
    if (!groups[item.section]) {
      groups[item.section] = [];
    }
    groups[item.section].push(item);
    return groups;
  }, {});

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 2.5, py: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.74)", fontWeight: 800, letterSpacing: "0.22em", fontSize: 12 }}>
            SMARTDOCTORS
          </Typography>
          <IconButton size="small" sx={{ color: "rgba(255,255,255,0.85)" }}>
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            p: 2.2,
            borderRadius: "32px",
            border: "1px solid rgba(255,255,255,0.14)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 100%)",
          }}
        >
          <Box sx={{ display: "grid", justifyItems: "center", gap: 1.25 }}>
            <Avatar sx={{ width: 86, height: 86, bgcolor: "rgba(255,255,255,0.12)", color: "#fff" }}>
              {(session?.name || "G").slice(0, 1)}
            </Avatar>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, textAlign: "center" }}>{session?.name || "Gast"}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.66)", fontSize: 13, textAlign: "center" }}>
                {session?.role || "Session"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {Object.entries(groupedItems).map(([section, items], index) => (
          <Box key={section}>
            {index > 0 ? <Divider sx={{ borderColor: "rgba(255,255,255,0.22)", my: 1.5 }} /> : null}
            <Typography sx={{ px: 2, pb: 1, color: "rgba(255,255,255,0.64)", fontWeight: 800, letterSpacing: "0.14em", fontSize: 12 }}>
              {section.toUpperCase()}
            </Typography>
            <List sx={{ px: 0.75 }}>
              {items.map((item) => {
                const Icon = item.icon;
                const selected = location.pathname === item.to;

                return (
                  <ListItemButton
                    key={item.to}
                    component={NavLink}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      borderRadius: "22px",
                      mb: 0.75,
                      color: selected ? "#fff" : "rgba(255,255,255,0.82)",
                      "&.active": {
                        background: "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.10) 100%)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                      <Icon />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 3,
            color: "rgba(255,255,255,0.82)",
            background: "rgba(255,255,255,0.08)",
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <LogoutRoundedIcon />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          display: { xs: "flex !important", lg: "none !important" },
          backdropFilter: "blur(18px)",
          background: "rgba(255,255,255,0.66)",
          borderBottom: "1px solid rgba(219,227,239,0.9)",
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={() => setMobileOpen(true)}>
            <MenuRoundedIcon />
          </IconButton>
          <Typography variant="h6">Gada</Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, width: { lg: `calc(100% - ${drawerWidth}px)` }, pt: { xs: 8, lg: 0 } }}>
        {children}
      </Box>
    </Box>
  );
}
